/**
 * @file transfer-mixin.ts
 *
 * Mesh I/O for as-app: retrieval from the configurator, download, app-to-app
 * transfer, the autoload dialog, mesh history and the transfer modal's click
 * routing. On connect it loads any pending transfer from IDB and shows the
 * autoload dialog if one is waiting, and keeps the overlay in step with the
 * modals export plugins register on ExportBuilder.
 *
 * The overlay click handler is registered directly on as-modal-overlay so that
 * transfer modal interactions (app selection, autoload confirm/cancel, history
 * clear) are routed to _handleTxModalClick without bubbling through the host
 * element's own event listeners.
 *
 * Requires: ModalMixinMembers, ErrorMixinMembers, InitQueueMixinMembers
 * Exposes: meshData, openTransferInNewTab, exporter, createExporter,
 *          otherAppsForTransfer, onTransferMeshLoaded, transferMesh,
 *          downloadCurrentMesh, saveFileAs, clearTransferDB
 */
import {
  storeMesh,
  checkForStoredMeshData,
  setNewApp,
  deleteAllStoredTransfers,
  deleteMesh,
  getSenderApp,
  deleteStaleTransferMeshes,
  type StoredMeshData,
  type TransferSaveData,
} from '../../../features/db/db.ts';
import { appInfo, appInfoCached, getAppInfo, getFilteredAppsSync } from '../lib/appConfig.ts';
import { fullAppSuiteRootURL, isProduction, inIframe, noAPI, suitePageUrl } from '../../../data/constants.ts';
import { devlog, navigateViaAnchor, registerListeners, unregisterListeners } from '../../../util/utils.ts';
import { type FileInfoArr } from '../../../data/data-interfaces.ts';
import { ExportBuilder, type ExportBuilderOptions } from '../../../features/importExport/lib/exportBuilder.ts';
import * as FileSave from '../../../features/host/fileSave.ts';
import { asConfigurator } from '../../../util/singletons.ts';
import { sendEv } from '../../../data/events.ts';
import type { Project } from '../../../data/projectManagement.ts';
import type { UserData } from '../../../features/serverApi/asApi.ts';
import { type Constructor } from './mixin-types-utils.ts';
import { MODAL_SHOW_DELAY_MS, type ModalMixinMembers } from './modal-mixin.ts';
import type { ErrorMixinMembers } from './error-mixin.ts';
import type { InitQueueMixinMembers } from './init-queue-mixin.ts';

/** Detail handed to onTransferMeshLoaded (same shape as as-db-mesh-loaded's). */
export interface TransferMeshDetail {
  /** The transferred mesh as a plain File, named "<meshName>.<fileType>". */
  stl: File;
  /** Id of the stored IDB entry. */
  meshId: string;
  /** The full stored record. */
  mesh: StoredMeshData;
}

/**
 * Rx hook: process a received transfer mesh. Resolve -> as-mesh-rx-completed
 * is dispatched automatically (return { keepStored: true } to keep the stored
 * entry, i.e. shouldDelete: false). Reject -> a persistent error notification
 * offers "Skip transfer" / "Save transfer as file" instead.
 */
export type TransferMeshLoadedCallback =
  (detail: TransferMeshDetail) => Promise<{ keepStored?: boolean } | void> | { keepStored?: boolean } | void;

interface MeshData {
  latestMesh: StoredMeshData | null;
  /** A mesh transferred from another app that should be autoloaded. */
  autoLoadMesh: StoredMeshData | null;
  totalCount: number;
  meshes?: StoredMeshData[];
}

/** Members this mixin reads off the finished element. AsApp supplies all three,
 *  so none can be a constraint on the base. */
interface TransferHost {
  appId: string;
  userData: UserData | null;
  currentProject: Project | null;
  unsavedChanges: boolean;
  navigatingAway: boolean;
}

/** Download a blob straight to disk via a synthetic anchor click. Deliberately
 *  not saveFileAs: that routes through the host's provider (GrabCAD's native
 *  dialog), and these paths want a plain browser download. */
const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Surface added by TransferMixin. */
export interface TransferMixinMembers {
  meshData: MeshData;
  openTransferInNewTab: boolean;
  /** Shared export builder. Configure it (clear + addFile / addConfiguratorMesh / fromSchema) then call download() or sendToGrabCAD(). */
  exporter: ExportBuilder;
  /** Create an independent one-shot builder that won't affect the shared exporter. */
  createExporter(opts?: ExportBuilderOptions): ExportBuilder;
  readonly otherAppsForTransfer: string[];
  /**
   * Rx hook for transferred meshes. When set (assign during app setup, before
   * calling init() on <as-configurator>), as-db-mesh-loaded is NOT dispatched;
   * the callback is awaited instead. Resolve -> as-mesh-rx-completed is sent
   * automatically (return { keepStored: true } for shouldDelete: false).
   * Reject -> a persistent error notification offers "Skip transfer" /
   * "Save transfer as file".
   */
  onTransferMeshLoaded: TransferMeshLoadedCallback | null;
  /** Start a mesh transfer to another app without going through the transfer
   *  modal. For automated flows like autoShadowBoard. */
  transferMesh(destinationAppId: string): void;
  /**
   * Save an arbitrary file to disk using the mechanism appropriate for the
   * current environment (e.g. GrabCAD's native save dialog when hosted there,
   * a browser download otherwise). Resolves once the host reports the result —
   * await it before e.g. closing the window. success is false when the user
   * cancelled the native dialog.
   *
   * For exporting files, use ExportBuilder.download() instead — it resolves
   * the builder, handles single-file/ZIP packaging, and routes through the
   * same environment-aware save.
   */
  saveFileAs(blob: Blob, filename: string, opts?: FileSave.FileSaveOptions): Promise<FileSave.FileSaveResult>;
  /** Download the current configurator mesh. One resolved file downloads as
   *  is; more than one is zipped. */
  downloadCurrentMesh(fileName: string, finalize?: boolean, opts?: ExportBuilderOptions): Promise<void>;
  /** Delete every stored transfer from IndexedDB. */
  clearTransferDB(): Promise<void>;
}

/**
 * Why the app is leaving. Rides along on the auto_redirect_timeout log so a
 * stalled exit can be told apart from a stalled app-to-app transfer.
 */
export type RedirectReason = 'transfer' | 'exit_to_host';

/** Internal transfer methods other mixins call. Separate from
 *  TransferMixinMembers so the public surface above stays the app-facing API. */
export interface TransferMixinInternals {
  _prepareTransferMesh(): Promise<void>;
  _redirectToApp(url: string, newTab: boolean, label?: string, reason?: RedirectReason): void;
}

export const TransferMixin = <T extends Constructor<HTMLElement & ModalMixinMembers & ErrorMixinMembers & InitQueueMixinMembers>>(Base: T) =>
  class TransferMixin extends Base {
    meshData: MeshData = { latestMesh: null, autoLoadMesh: null, totalCount: 0 };
    openTransferInNewTab: boolean = false;
    onTransferMeshLoaded: TransferMeshLoadedCallback | null = null;

    _transferSelfListeners: Record<string, EventListener> = {};
    _transferWindowListeners: Record<string, EventListener> = {};
    _overlayClickHandler: EventListener = (e) => { this._handleTxModalClick(e as MouseEvent); };
    _onExportDescriptorRegistered = (): void => this._syncPluginModals();

    /** Shared export builder. Configure it (clear + addFile) then call download() or sendToGrabCAD(). */
    exporter!: ExportBuilder;

    /** AsApp's own members, none of which this mixin can constrain its base on.
     *  One cast, read through a named type. */
    get _host(): TransferHost { return this as unknown as TransferHost; }

    /** Create an independent one-shot builder. Use when you need an isolated instance that won't affect the shared exporter. */
    createExporter(opts: ExportBuilderOptions = {}): ExportBuilder {
      return new ExportBuilder(this._host.appId, opts);
    }

    get otherAppsForTransfer(): string[] {
      const appId = this._host.appId;
      const currentAppCfg = appInfo[appId];
      const exclude = currentAppCfg?.allowSelfTransfer ? [] : [appId];
      return getFilteredAppsSync('all', true, exclude);
    }

    transferMesh(destinationAppId: string): void {
      this._autoTransferToApp(destinationAppId);
    }

    saveFileAs(blob: Blob, filename: string, opts: FileSave.FileSaveOptions = {}): Promise<FileSave.FileSaveResult> {
      return FileSave.saveFileAs(blob, filename, opts);
    }

    async downloadCurrentMesh(fileName: string, finalize = true, opts: ExportBuilderOptions = {}): Promise<void> {
      const builder = this.createExporter(opts);
      builder.addConfiguratorMesh({ fileName, customData: { finalize } });
      await builder.download({ filename: fileName });
    }

    clearTransferDB(): Promise<void> { return deleteAllStoredTransfers(); }

    // ------------------------------------------------------------------
    // Mesh retrieval and download
    // ------------------------------------------------------------------

    _getMeshFromConfigurator(finalized = false, type = 'stl', filename = 'model.stl'): Promise<Blob> {
      devlog('getMeshFromConfigurator: Starting mesh request', { finalized, type, filename });
      return new Promise<Blob>((resolve, reject) => {
        this.addToPostInitQueue(() => {
          sendEv(window, 'as-get-configurator-mesh', {
            detail: {
              finalized, type, filename,
              resolve: (blob: Blob) => {
                devlog('getMeshFromConfigurator: Received mesh blob', blob);
                if (!blob) {
                  const error = Object.assign(new Error('Invalid mesh blob'), { name: 'MeshRequestError' });
                  reject(error);
                  return;
                }
                resolve(blob);
              },
              reject: (err: Error) => {
                console.error('getMeshFromConfigurator: Error getting mesh:', err);
                reject(Object.assign(err || new Error('Failed to get mesh'), { name: err?.name || 'MeshFetchError' }));
              },
            }
          });
        });
      });
    }

    /** As _getMeshFromConfigurator, with the "preparing" modal up while it runs. */
    _meshFromConfigurator(finalized = false, type = 'stl', filename = 'model.stl'): Promise<Blob> {
      devlog('meshFromConfigurator: Starting mesh request with modal', { finalized, type, filename });
      this.modalContent = { type: 'saving', data: { status: 'Preparing mesh for download...' } };
      this.showModal();
      return this._getMeshFromConfigurator(finalized, type, filename);
    }

    async _downloadMesh(finalized = false, type = 'stl', fileHandle?: any): Promise<void> {
      try {
        let blob: Blob;
        if (fileHandle) {
          blob = await this._getMeshFromConfigurator(finalized, type, fileHandle.name);
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          devlog('Mesh saved successfully to:', fileHandle.name);
        } else {
          blob = await this._getMeshFromConfigurator(finalized, type, `mesh.${type}`);
          downloadBlob(blob, `mesh.${type}`);
        }
        sendEv(this, 'as-data-export', {
          detail: { subcategory: 'local', fileInfo: [{ sizeBytes: blob.size, fileType: type }] },
        });
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        this.logError('mesh-download', error, 'critical', 'Error downloading mesh:');
        sendEv(this, 'as-data-export', {
          detail: { subcategory: 'local', error: error?.message ?? String(error), fileInfo: [{ fileType: type }] }
        });
        sendEv(this, 'as-notify', {
          bubbles: false,
          detail: { defaultText: 'Error downloading mesh: {0}', dictKey: 'notifications.mesh.download.error', vars: [error.message], type: 'error' }
        });
        this.showErrorModal({ error, info: 'Failed to download mesh', origin: 'mesh-download' });
      }
    }

    // ------------------------------------------------------------------
    // Autoload
    // ------------------------------------------------------------------

    _showAutoLoadDialog(autoLoadMesh: StoredMeshData): void {
      devlog('Checking for auto-load dialog requirements for mesh:', autoLoadMesh.meshName);
      this.modalContent = { type: 'autoload', data: { autoLoadMesh } };
      if (this.hasAttribute('confirm-autoload')) {
        devlog('confirm-autoload attribute is present, showing dialog');
        this.showModal();
      } else {
        devlog('confirm-autoload attribute is not present, automatically loading mesh');
        this._handleAutoLoadConfirm();
      }
    }

    _handleAutoLoadConfirm(): void {
      const mesh = this.modalContent.data?.autoLoadMesh;
      if (!mesh) { console.error('No auto-load mesh found in modal data'); this.modalOverlay?.close(); return; }
      this.modalOverlay?.close();

      if (mesh.stlData) {
        devlog('Dispatching as-db-mesh-loaded event with STL data, size:', mesh.stlData.size);
        // `mesh.meshName` is set once at the mesh's very first hop (see
        // _prepareTransferMesh/_autoTransferToApp: the project-derived name) and
        // then carried forward unchanged on every subsequent transfer, so it
        // always names the original sender, not the most recent one. `lastApp` is
        // the field that actually gets rewritten on each hop (see storeMesh) —
        // use that for "from".
        sendEv(this, 'as-notify', {
          bubbles: false,
          detail: { defaultText: 'Detected mesh from {0}: {1}', dictKey: 'notifications.mesh.autoload.detected', vars: [mesh.lastApp, mesh.meshName], type: 'info' }
        });
        const meshWithDelete = { ...mesh, queueDelete: true };
        // Paramate picks the importer from the filename, so name the format the
        // record says it holds. No fileType means a sender before 0.19, so STL.
        const meshType = mesh.fileType ?? 'stl';
        const stlFile = new File([mesh.stlData], `${mesh.meshName}.${meshType}`, { type: 'application/octet-stream' });
        this.addToPostInitQueue(() => {
          // With an onTransferMeshLoaded callback set, the callback owns the Rx
          // flow and the event is suppressed entirely — dispatching both would
          // invite double handling (two uploads of the same mesh).
          if (this.onTransferMeshLoaded) {
            this._runTransferMeshCallback({ mesh: meshWithDelete, meshId: mesh.meshId, stl: stlFile });
            return;
          }
          sendEv(this, 'as-db-mesh-loaded', {
            detail: { mesh: meshWithDelete, meshId: mesh.meshId, stl: stlFile }
          });
        });
      } else {
        console.warn('Mesh has no STL data to load');
        setNewApp(mesh.meshId, this._host.appId, false)
          .then(() => devlog('Reset autoLoad flag for mesh without STL data'))
          .catch(err => console.error('Error resetting autoLoad flag:', err));
      }
    }

    _handleAutoLoadCancel(): void {
      const mesh = this.modalContent.data?.autoLoadMesh;
      if (mesh) {
        setNewApp(mesh.meshId, this._host.appId, false)
          .then(() => devlog('Reset autoLoad flag after user canceled auto-load'))
          .catch(err => console.error('Error resetting autoLoad flag:', err));
      }
      this.modalOverlay?.close();
    }

    /** Await the app's onTransferMeshLoaded and complete (or fail) the Rx flow for it. */
    async _runTransferMeshCallback(detail: TransferMeshDetail): Promise<void> {
      try {
        const result = await this.onTransferMeshLoaded!(detail) as { keepStored?: boolean } | undefined;
        sendEv(this, 'as-mesh-rx-completed', {
          bubbles: false,
          detail: { meshId: detail.meshId, shouldDelete: !result?.keepStored }
        });
      } catch (err) {
        this.logError('mesh-rx-callback', err, 'regular', 'onTransferMeshLoaded rejected:');
        // Log the failed interapp import right away — the user may resolve the
        // notification much later, or never (X-dismiss retries on next boot).
        this.sendLog('data_import_error', 'interapp', {
          error: err instanceof Error ? err.message : String(err),
          fromApp: detail.mesh.lastApp,
          meshId: detail.meshId,
          meshName: detail.mesh.meshName,
          fileInfo: [{ sizeBytes: detail.stl.size, fileType: detail.mesh.fileType ?? 'stl' }],
        });
        this._showTransferRxErrorNotification(detail, err);
      }
    }

    /**
     * Persistent error notification for a rejected onTransferMeshLoaded.
     * Both actions close the card themselves (via the id handed back through the
     * as-notify resolve callback) once they have completed the Rx flow:
     *   Skip transfer          keeps the stored mesh but clears its auto-load flag
     *                          (shouldDelete: false) — no retry loop on next boot,
     *                          still recoverable from the history modal.
     *   Save transfer as file  downloads the STL, then deletes the stored entry.
     * Dismissing via the X decides nothing: the entry stays flagged for auto-load
     * and the transfer is retried on the next boot.
     */
    _showTransferRxErrorNotification(detail: TransferMeshDetail, err: unknown): void {
      let notifId = '';
      const message = err instanceof Error ? err.message : String(err);
      const finish = (shouldDelete: boolean) => {
        // error in the detail marks this as a failure resolution: the cleanup path
        // still runs, but skips the success data_import log (data_import_error was
        // already sent when the callback rejected).
        sendEv(this, 'as-mesh-rx-completed', {
          bubbles: false,
          detail: { meshId: detail.meshId, shouldDelete, error: message }
        });
        if (notifId) sendEv(this, 'as-close-notification', { bubbles: false, detail: { id: notifId } });
      };
      sendEv(this, 'as-notify', {
        bubbles: false,
        detail: {
          defaultText: 'Could not load the transferred mesh: {0}',
          dictKey: 'notifications.mesh.rx.loadFailed',
          vars: [message],
          type: 'error',
          persist: true,
          resolve: (id: string) => { notifId = id; },
          actions: [
            {
              defaultText: 'Skip transfer',
              dictKey: 'notifications.mesh.rx.skip',
              callback: () => finish(false),
            },
            {
              defaultText: 'Save transfer as file',
              dictKey: 'notifications.mesh.rx.saveAs',
              callback: () => {
                downloadBlob(detail.stl, detail.stl.name);
                finish(true);
              },
            },
          ],
        }
      });
    }

    // ------------------------------------------------------------------
    // History and cleanup
    // ------------------------------------------------------------------

    async _cleanUpStaleMeshes(): Promise<void> {
      try {
        const deleted = await deleteStaleTransferMeshes();
        if (deleted > 0) {
          devlog(`cleanUpStaleMeshes: Deleted ${deleted} stale mesh(es) older than 24 h`);
          this.sendLog('stale_meshes_deleted', { count: deleted });
        }
      } catch (err) {
        this.logError('mesh-stale-cleanup', err, 'warning', 'Error cleaning up stale meshes:');
      }
    }

    async _handleShowStoredMeshes(): Promise<void> {
      devlog('Received show-stored-meshes event, loading mesh history');
      const meshData = await checkForStoredMeshData(this._host.appId, false);
      if (meshData.totalCount > 0) {
        devlog(`Found ${meshData.totalCount} mesh(es) for app ${this._host.appId}`);
        this.modalContent = { type: 'history', data: { meshes: meshData.meshes } };
        this.showModal();
      } else {
        devlog('No mesh history found for this app');
      }
    }

    async _postMeshReceiveCleanup(event: Event): Promise<void> {
      const { meshId, shouldDelete, error } = (event as CustomEvent).detail;
      if (!meshId) { console.error('No meshId provided in mesh-processed event'); return; }

      const senderApp = await getSenderApp(meshId);
      if (senderApp === null) { console.error('No sender app found for mesh: ', meshId); return; }

      // A failure resolution (error set) was already logged as data_import_error
      // when the Rx callback rejected — don't also log a successful import.
      if (!error) this.sendLog('data_import', 'interapp', { fromApp: senderApp });
      try {
        if (shouldDelete !== false) {
          devlog(`Deleting mesh ${meshId} as requested`);
          await deleteMesh(meshId);
        } else {
          devlog(`Setting autoLoad to false for mesh ${meshId}`);
          await setNewApp(meshId, this._host.appId, false);
        }
      } catch (err) {
        this.logError('mesh-processing', err, 'critical', 'Error processing mesh:');
        this.showErrorModal({ error: err as Error, info: 'Failed to process mesh', origin: 'mesh-processing' });
      }
    }

    // ------------------------------------------------------------------
    // Transfer
    // ------------------------------------------------------------------

    /**
     * Default name for a mesh with no prior transfer history (i.e. its very first
     * hop). Deliberately doesn't embed the sending app: unlike `lastApp`, this
     * name is carried forward unchanged on every later hop, so baking the sender
     * in here would go stale — and duplicate `lastApp` — after hop 1 (see the
     * "Detected mesh" notification in _handleAutoLoadConfirm). Prefers the actual
     * project name so the transferred mesh is identifiable; falls back to a dated
     * placeholder only when there's no project to name it after.
     */
    _defaultMeshName(): string {
      const projectName = this._host.currentProject?.metadata.name;
      return projectName
        ? `${projectName.trim().replace(/\s+/g, '-')}-mesh`
        : `untitled-mesh-${new Date().toISOString().slice(0, 10)}`;
    }

    /** The record a transfer stores. Reuses the existing mesh's identity when
     *  this is a later hop, so meshId/meshName/authorId stay with the mesh. */
    _buildTransferData(destinationApp: string): TransferSaveData {
      const latest = this.meshData.latestMesh;
      if (latest) {
        return {
          authorId: latest.authorId,
          meshName: latest.meshName,
          meshId: latest.meshId,
          currentApp: this._host.appId,
          destinationApp,
          stlData: null,
        };
      }
      const meshId = `mesh-${this._host.appId}-${Date.now()}`;
      devlog('No existing mesh data found, created new mesh with ID:', meshId);
      return {
        authorId: this._host.userData?.eMail || 'unknown',
        meshName: this._defaultMeshName(),
        meshId,
        currentApp: this._host.appId,
        destinationApp,
        stlData: null,
      };
    }

    async _prepareTransferMesh(): Promise<void> {
      devlog('Starting transfer mesh process');
      this.openTransferInNewTab = false;

      const configInstance = asConfigurator();
      if (!configInstance) throw new Error('Configurator component not found');
      if (!configInstance.initialized) {
        sendEv(this, 'as-notify', {
          bubbles: false,
          detail: { defaultText: 'Cannot transfer mesh: the configurator is not ready yet. Please wait a moment and try again.', dictKey: 'notifications.mesh.transfer.notReady', type: 'error' }
        });
        return;
      }

      this.modalContent = { type: 'saving', data: { status: 'Preparing mesh for transfer...' } };
      this.showModal({ delayMs: MODAL_SHOW_DELAY_MS });

      if (!appInfoCached) await getAppInfo();

      let availableApps = this.otherAppsForTransfer;
      if (availableApps.length === 0) {
        console.warn('[AS-APP] No apps available for transfer on first attempt. Retrying appInfo load. appInfoCached:', appInfoCached);
        try {
          await getAppInfo();
          availableApps = this.otherAppsForTransfer;
        } catch (error) {
          console.error('[AS-APP] Failed to load appInfo on retry:', error);
        }
        if (availableApps.length === 0) {
          console.error('[AS-APP] No apps available for transfer after retry. appInfoCached:', appInfoCached);
          sendEv(this, 'as-notify', {
            bubbles: false,
            detail: { defaultText: 'Cannot transfer mesh: no apps are available for transfer. The app configuration may not be loaded yet.', dictKey: 'notifications.mesh.transfer.no-apps.error', type: 'error' }
          });
          this.modalOverlay?.close();
          return;
        }
      }

      const pendingStoreData = this._buildTransferData('none');

      devlog('Getting mesh from configurator...');
      this._meshFromConfigurator(true, 'stl', `${pendingStoreData.meshName}.stl`)
        .then(blob => {
          devlog('Got mesh blob, size:', blob.size);
          pendingStoreData.stlData = blob;
          this.modalContent = { type: 'success', data: { blob, availableApps, pendingStoreData } };
          this.showModal();
        })
        .catch(err => {
          this.logError('mesh-transfer', err, 'critical', 'Error in mesh transfer process:');
          this.sendLog('data_export_error', 'interapp', { error: err?.message ?? String(err) });
          this.showErrorModal({ error: err, info: 'Error occurred during mesh transfer operation (meshFromConfigurator)', origin: 'mesh-transfer' });
          sendEv(this, 'as-notify', {
            bubbles: false,
            detail: { defaultText: 'Error during mesh transfer: {0}', dictKey: 'notifications.mesh.transfer.failed', vars: [err.message], type: 'error' }
          });
          this.modalOverlay?.close();
        });
    }

    /**
     * Redirect to another app via a deferred anchor click (see navigateViaAnchor
     * for why this can't be a plain location assignment). A same-tab redirect
     * normally navigates away well within 2s; if that hasn't happened yet (a slow
     * host, or the webview silently dropping the click), a "Redirecting…" spinner
     * replaces whatever modal state is showing so the app doesn't just look stuck.
     * A new-tab redirect never navigates this tab away, so both timers are cleared
     * right after the click fires instead of lingering forever.
     *
     * Observed in GrabCAD's embedded webview: later hops of a manual multi-app
     * transfer chain can still silently drop the synthetic click even with
     * identical timing/handling to a hop that worked — looks like a host-side
     * quirk, not something fixable from this side. If the indicator has been up
     * a while with no navigation, a real, directly-clickable link is shown as a
     * fallback (see getSavingModalContent) — an actual user click on it is as
     * close to a normal link click as the DOM can produce.
     *
     * Root cause of the earlier "nothing happens, not even on a real click"
     * reports: as-app's `_onBeforeUnload` cancels ANY navigation attempt (real or
     * synthetic) in the GrabCAD host whenever `unsavedChanges` is true or the app
     * is busy — the host has no "leave site?" prompt UI, so the veto is silent,
     * with no console error. Every navigation started here is a deliberate "leave
     * this app now" action, so both halves of that guard are stood down first:
     * `unsavedChanges` is cleared (the user has already answered that question by
     * the time we get here) and `navigatingAway` is latched, which covers the busy
     * half — a page stuck busy is exactly the page a user most wants to leave, and
     * waiting for it to go idle isn't an option when it never does.
     */
    _redirectToApp(url: string, newTab: boolean, label = 'Continue', reason: RedirectReason = 'transfer'): void {
      // Must happen before the anchor click below, not just before it — clearing
      // these only at click time would still race a `beforeunload` fired by that
      // same click. See the root-cause note above.
      this._host.unsavedChanges = false;
      this._host.navigatingAway = true;

      const showIndicator = setTimeout(() => {
        this.modalContent = { type: 'saving', data: { status: 'Redirecting…' } };
        this.showModal();
      }, 2000);
      const showFallbackLink = setTimeout(() => {
        this.sendLog('auto_redirect_timeout', 'interapp', { url, reason }).catch((err: unknown) => console.error('sendLog failed (auto_redirect_timeout):', err));
        this.modalContent = { type: 'saving', data: { status: 'Redirecting…', link: { href: url, label, newTab } } };
        this.showModal();
      }, 6000);
      setTimeout(() => {
        navigateViaAnchor(url, newTab);
        if (newTab) { clearTimeout(showIndicator); clearTimeout(showFallbackLink); }
      }, 0);
    }

    async _autoTransferToApp(destinationAppId: string): Promise<void> {
      devlog(`Starting automated transfer mesh to: ${destinationAppId}`);
      this.openTransferInNewTab = false;

      const configInstance = asConfigurator();
      if (!configInstance || !configInstance.initialized) {
        console.error('Configurator not ready for auto-transfer');
        return;
      }

      // Set modal to saving state to show progress
      this.modalContent = { type: 'saving', data: { status: `Transferring to ${destinationAppId}...` } };
      this.showModal({ delayMs: MODAL_SHOW_DELAY_MS });

      const pendingStoreData = this._buildTransferData(destinationAppId);

      try {
        const blob = await this._getMeshFromConfigurator(true, 'stl', `${pendingStoreData.meshName}.stl`);
        pendingStoreData.stlData = blob;

        const savedMeshData = await storeMesh(pendingStoreData, true);
        devlog('Auto-transfer mesh saved:', savedMeshData.meshId);

        this.meshData.latestMesh = savedMeshData;
        this.modalOverlay?.close();

        // Fire-and-forget, same reasoning as _handleTxModalClick: awaiting this
        // network call before navigating loses the activation window the GrabCAD
        // embedded webview requires for a script-triggered anchor click to work.
        this.sendLog('data_export', 'interapp', { toApp: destinationAppId, auto: true }).catch((err: unknown) => console.error('sendLog failed (data_export interapp, auto):', err));

        const currentUrl = new URL(window.location.href);
        const newAppRedirURL = new URL(suitePageUrl(`${fullAppSuiteRootURL}/apps/${destinationAppId}`));

        // Propagate all current search params
        currentUrl.searchParams.forEach((val, key) => newAppRedirURL.searchParams.set(key, val));

        // Ensure 'mode' is preserved if it was in the fragment but logic detected it
        if (window.location.href.includes('mode=autoShadowBoard') && !newAppRedirURL.searchParams.has('mode')) {
          newAppRedirURL.searchParams.set('mode', 'autoShadowBoard');
        }

        const newTab = currentUrl.searchParams.get('newTab') === 'true';
        if (!isProduction) devlog('auto redir to: ', newAppRedirURL.toString());
        // Anchor click rather than window.open / location.href so the transfer
        // redirect also works in the GrabCAD embedded webview (see navigateViaAnchor
        // and _redirectToApp for the deferred-click / stuck-redirect handling).
        this._redirectToApp(newAppRedirURL.toString(), newTab, `Continue to ${appInfo[destinationAppId]?.appNameDefaultText ?? destinationAppId}`);

      } catch (err) {
        this.logError('auto-mesh-transfer', err, 'critical', 'Error in auto mesh transfer:');
        this.sendLog('data_export_error', 'interapp', { toApp: destinationAppId, auto: true, error: (err as Error)?.message ?? String(err) });
        this.showErrorModal({ error: err as Error, info: 'Failed to complete automated transfer', origin: 'auto-mesh-transfer' });
      }
    }

    // ------------------------------------------------------------------
    // Transfer modal click routing (delegated from as-app-overlay)
    // ------------------------------------------------------------------

    async _handleTxModalClick(event: MouseEvent): Promise<void> {
      const target = event.target as HTMLElement;
      if (!target.closest('[id^="modal-panel-"]')) return;

      // The stuck-redirect fallback link (see getSavingModalContent). Take the click
      // over and re-issue it from a light-DOM anchor — this one lives in as-app's
      // shadow root, so on its own it never reaches the GrabCAD host as a link. The
      // modal closes either way: if the host drops this attempt too, the user is left
      // with a usable app instead of a spinner they can't dismiss.
      const fallbackLink = target.closest('#redirect-fallback-link') as HTMLAnchorElement | null;
      if (fallbackLink) {
        event.preventDefault();
        const { href, target: linkTarget } = fallbackLink;
        this.modalOverlay?.close();
        navigateViaAnchor(href, linkTarget === '_blank');
        return;
      }

      const newTabInput = target.closest('#transfer-input-open-in-new-tab') as HTMLInputElement | null;
      if (newTabInput) {
        this.openTransferInNewTab = newTabInput.checked;
        devlog('Transfer input open in new tab', this.openTransferInNewTab);
      }

      const appButton = target.closest('.app-selection-button');
      if (appButton) {
        event.preventDefault();
        const selectedAppId = appButton.getAttribute('data-app');
        devlog('Selected app:', selectedAppId);
        if (selectedAppId && this.modalContent.type === 'success') {
          await this._transferToSelectedApp(selectedAppId);
        }
        return;
      }

      if (this.modalContent.type === 'autoload') {
        if (target.closest('#confirm-autoload')) { devlog('User confirmed auto-load'); this._handleAutoLoadConfirm(); return; }
        if (target.closest('#cancel-autoload')) { devlog('User canceled auto-load'); this._handleAutoLoadCancel(); return; }
      }

      const clearButton = target.closest('#clear-mesh-history');
      if (clearButton && this.modalContent.type === 'history') {
        devlog('Clear mesh history button clicked');
        deleteAllStoredTransfers()
          .then(() => {
            devlog('All mesh history cleared');
            this.modalOverlay?.close();
            this.meshData = { latestMesh: null, autoLoadMesh: null, totalCount: 0 };
            devlog('Cleared mesh history, refreshing page to ensure clean state');
            window.location.reload();
          })
          .catch(err => {
            this.logError('mesh-history-clear', err, 'critical', 'Error clearing mesh history:');
            this.showErrorModal({ error: err, info: 'Failed to clear mesh history', origin: 'mesh-history' });
          });
      }
    }

    /** Commit the transfer the user picked an app for: store the pending mesh (or
     *  just re-point the existing one) and redirect. */
    async _transferToSelectedApp(selectedAppId: string): Promise<void> {
      const pendingData = this.modalContent.data?.pendingStoreData;

      const redirect = (someAppInfo: typeof appInfo[string] | undefined, url: string) => {
        if (!someAppInfo?.id) { console.warn(`No app link defined for ${selectedAppId}`); return; }
        // Anchor click so the redirect also works in the GrabCAD embedded webview
        // (see navigateViaAnchor and _redirectToApp for the deferred-click /
        // stuck-redirect handling).
        this._redirectToApp(url, this.openTransferInNewTab, `Continue to ${someAppInfo.appNameDefaultText ?? selectedAppId}`);
      };
      const redirUrl = suitePageUrl(`${fullAppSuiteRootURL}/apps/${selectedAppId}`, window.location.search);

      if (pendingData) {
        pendingData.destinationApp = selectedAppId;
        this.modalContent = { type: 'saving', data: { status: 'Saving mesh to database...' } };
        this.showModal({ delayMs: MODAL_SHOW_DELAY_MS });

        try {
          const savedMeshData = await storeMesh(pendingData, true);
          devlog('Mesh saved successfully:', savedMeshData.meshId, 'autoLoad:', savedMeshData.autoLoad);
          this.meshData.latestMesh = savedMeshData;
          this.modalOverlay?.close();

          const someAppInfo = appInfo[selectedAppId];
          devlog(someAppInfo, appInfo);
          // Fire-and-forget: awaiting this network call before navigating burns
          // through the window the GrabCAD embedded webview allows for a
          // script-triggered anchor click to actually navigate (a live-console
          // a.click() with no pending user activation at all fails outright in
          // that webview, so a several-hundred-ms-to-seconds network round trip
          // between the click and the anchor click is enough to lose it too).
          this.sendLog('data_export', 'interapp', { toApp: selectedAppId }).catch((err: unknown) => console.error('sendLog failed (data_export interapp):', err));

          if (!isProduction) devlog('redir to: ', redirUrl);
          if (this.openTransferInNewTab) devlog('Opening in new tab');
          redirect(someAppInfo, redirUrl);
        } catch (err: any) {
          this.logError('mesh-storage', err, 'critical', 'Error saving mesh to database:');
          this.sendLog('data_export_error', 'interapp', { toApp: selectedAppId, error: err?.message ?? String(err) });
          this.showErrorModal({ error: err, info: 'Failed to save mesh to database', origin: 'mesh-storage' });
        }
        return;
      }

      if (this.meshData.latestMesh) {
        try {
          await setNewApp(this.meshData.latestMesh.meshId, selectedAppId, true);
          devlog('Updated mesh destination app to:', selectedAppId, 'with autoLoad=true');
          redirect(appInfo[selectedAppId], redirUrl);
        } catch (err: any) {
          this.logError('mesh-transfer-destination', err, 'critical', 'Error updating mesh destination:');
          this.sendLog('data_export_error', 'interapp', { toApp: selectedAppId, error: err?.message ?? String(err) });
          this.showErrorModal({ error: err, info: 'Failed to update mesh destination', origin: 'mesh-transfer' });
        }
      }
    }

    // ------------------------------------------------------------------
    // Plugin modals and lifecycle
    // ------------------------------------------------------------------

    /** Create a DOM element inside modalOverlay for any ExportBuilder-registered
     *  modal descriptor (e.g. as-modal-grabcad-success) not yet present. Safe to
     *  call repeatedly — skips descriptors already represented. */
    _syncPluginModals(): void {
      for (const desc of ExportBuilder.getModalDescriptors()) {
        if (!this.modalOverlay?.querySelector(`#${desc.id}`)) {
          const el = document.createElement(desc.tagName);
          el.id = desc.id;
          if (desc.disableBgClose) el.setAttribute('disable-bg-close', '');
          this.modalOverlay?.appendChild(el);
        }
      }
    }

    async connectedCallback() {
      await super.connectedCallback?.();

      if (!inIframe && !noAPI) {
        getAppInfo().catch((error: unknown) => console.error('Failed to pre-warm appInfo:', error));
      }

      const appId = this._host.appId;

      this.exporter = new ExportBuilder(appId);

      // A plugin (e.g. grabcadPlugin.ts) can call ExportBuilder.registerModal()
      // at any point relative to when this connectedCallback runs — depending on
      // a given app's own bundling/module-loading setup, that can land before or
      // after this line, with no reliable ordering guarantee either way (neither
      // an immediate call nor a microtask-deferred retry were reliably sufficient
      // in practice across every app's build). Three layers, cheapest first:
      // immediate (covers the common case), the 'as-export-descriptor-registered'
      // event (see registerModal()'s doc comment — covers a plugin registering
      // later while this listener is already attached), and finally window's
      // 'load' event as an unconditional catch-all — by spec it fires strictly
      // after every module script (regardless of how many separate files/fetches
      // were involved) has finished executing, so any registration missed by the
      // first two is guaranteed to be visible by then. Without this,
      // as-modal-grabcad-success could permanently never exist to show GrabCAD
      // transfer results, even though the transfer itself completes fine. The
      // "already present" guard in _syncPluginModals() keeps all of this idempotent.
      this._syncPluginModals();
      window.addEventListener('as-export-descriptor-registered', this._onExportDescriptorRegistered);
      window.addEventListener('load', this._onExportDescriptorRegistered, { once: true });

      await this._cleanUpStaleMeshes();
      this.meshData = await checkForStoredMeshData(appId, true, true) as MeshData;
      if (this.meshData.autoLoadMesh) {
        this._showAutoLoadDialog(this.meshData.autoLoadMesh);
      }

      this._transferSelfListeners = {
        'show-stored-meshes':   () => this._handleShowStoredMeshes().catch((e: unknown) => this.logError('show-stored-meshes', e)),
        'mesh-processed':       (e) => this._postMeshReceiveCleanup(e).catch((err: unknown) => this.logError('mesh-processed', err)),
        'as-mesh-rx-completed': (e) => this._postMeshReceiveCleanup(e).catch((err: unknown) => this.logError('as-mesh-rx-completed', err)),
        'as-transfer-mesh':     () => this._prepareTransferMesh().catch((e: unknown) => this.logError('as-transfer-mesh', e)),
        // Unified data-I/O logging. Every import path -> data_import, every export
        // path -> data_export, each tagged with a subcategory ('local' | 'interapp'
        // | 'grabcad'). Producers put subcategory + fileInfo in the event detail and
        // this layer just forwards it; mesh-vs-file is carried in fileInfo[].fileType.
        // Failures stay on the same events via detail.error, but are logged under
        // the distinct data_import_error / data_export_error types (error kept in
        // the payload), so success rates fall out of the event names directly.
        'as-files-imported': (ev: Event) => {
          const e = ev as CustomEvent<{ fileInfo?: FileInfoArr; fileInfoArr?: FileInfoArr; info?: string; error?: string }>;
          const fileInfo = e.detail.fileInfo ?? e.detail.fileInfoArr ?? [];
          this.sendLog(e.detail.error ? 'data_import_error' : 'data_import', 'local',
            { fileInfo, ...(e.detail.info && { info: e.detail.info }), ...(e.detail.error && { error: e.detail.error }) });
        },
        'as-data-import': (ev: Event) => { const { subcategory = 'local', ...rest } = (ev as CustomEvent).detail ?? {}; this.sendLog(rest.error ? 'data_import_error' : 'data_import', subcategory, rest); },
        'as-data-export': (ev: Event) => { const { subcategory = 'local', ...rest } = (ev as CustomEvent).detail ?? {}; this.sendLog(rest.error ? 'data_export_error' : 'data_export', subcategory, rest); },
        'as-log-to-server':    (ev: Event) => { const { evName, data } = (ev as CustomEvent).detail; this.sendLog(evName, data); },
        'error': (event: Event) => {
          this.logError((event as CustomEvent).detail.origin ?? 'component', (event as CustomEvent).detail.error, 'critical', 'Got error');
          this.showErrorModal((event as CustomEvent).detail);
        },
        'get-available-downloads': (event: Event) => {
          (event as CustomEvent).detail.resolve({ app: appId, downloads: 10 });
        },
      } as Record<string, EventListener>;

      this._transferWindowListeners = {
        'as-download-mesh': (event: Event) => {
          const { finalized = false, type = 'stl', fileHandle } = (event as CustomEvent).detail || {};
          this._downloadMesh(finalized, type, fileHandle).catch((e: unknown) => this.logError('as-download-mesh', e));
        },
      } as Record<string, EventListener>;

      registerListeners(this, this._transferSelfListeners);
      registerListeners(window, this._transferWindowListeners);
      if (this.modalOverlay) this.modalOverlay.addEventListener('click', this._overlayClickHandler);
    }

    disconnectedCallback() {
      unregisterListeners(this, this._transferSelfListeners);
      unregisterListeners(window, this._transferWindowListeners);
      window.removeEventListener('as-export-descriptor-registered', this._onExportDescriptorRegistered);
      window.removeEventListener('load', this._onExportDescriptorRegistered);
      if (this.modalOverlay) this.modalOverlay.removeEventListener('click', this._overlayClickHandler);
      super.disconnectedCallback?.();
    }
  };
